import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../api';

// Component for rendering a dropdown of areas based on a given location ID
const AreaDropdown = ({ 
  locationId, 
  value, 
  onChange, 
  className = '', 
  name = 'area_id', // Ensure matches backend expected field
  onValidationChange
}) => {
  // State to store filtered areas for the selected location
  const [areas, setAreas] = useState([]);
  // State to manage loading status
  const [loading, setLoading] = useState(false);
  // State to store error messages
  const [error, setError] = useState(null);
  // State to store all areas fetched from the API
  const [allAreas, setAllAreas] = useState([]);

  // Effect hook to fetch all areas from the API on component mount
  useEffect(() => {
    const fetchAllAreas = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get('/ems/areas');
        setAllAreas(response.data || []);
      } catch (err) {
        console.error('Error fetching areas:', err);
        setError('Failed to load areas. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllAreas();
  }, []);

  // Effect hook to filter areas based on locationId and update validation
  useEffect(() => {
    if (!locationId) {
      setAreas([]);
      onChange({ target: { name, value: '' } }); // ✅ Reset area if location empty
      if (onValidationChange) onValidationChange(false);
      return;
    }
    const filtered = allAreas.filter(area => String(area.location_id) === String(locationId));
    setAreas(filtered);
    if (onValidationChange) onValidationChange(filtered.length > 0 && !!value);
  }, [locationId, allAreas, value, onChange, name, onValidationChange]);

  // Handler for dropdown value changes
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange({ target: { name, value: newValue } });
    if (onValidationChange) onValidationChange(!!newValue);
  };

  // Render the dropdown with area options
  return (
    <>
      <select
        name={name}
        value={value || ''}
        onChange={handleChange}
        className={`form-select ${className}`}
        disabled={loading || !locationId}
      >
        <option value="">{loading ? 'Loading areas...' : 'Select Area'}</option>
        {error ? (
          <option value="" disabled>Error loading areas</option>
        ) : (
          areas.map((area) => (
            <option key={area.id} value={area.id}>
              {area.area_name} ({area.ems_location?.location_name || 'Unknown'})
            </option>
          ))
        )}
      </select>

      {error && <div className="text-danger small mt-1">{error}</div>}
      {!loading && locationId && areas.length === 0 && !error && (
        <div className="text-muted small mt-1">No areas found for this location</div>
      )}
    </>
  );
};

export default AreaDropdown;